<script>
import { mapGetters, mapMutations, mapState } from 'vuex';

import { getCurrencySymbol, MAX_AMOUNT } from '@/utils';

export default {
  name: 'FormDonationItems',
  props: {
    active: Boolean,
    showError: Boolean,
    staticForm: {
      type: Boolean,
      default: false
    },
    formCampaignSettings: {
      type: Object,
      default: () => null
    },
    localDonationItems: {
      type: Array,
      default: () => []
    },
    localDonationItemsOtherAmount: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      isOtherInputOpen: false,
      otherAmountValidationMessage: '',
      MAX_AMOUNT,
      failedImageUrls: {},
      expandedDescriptions: {},
      collapsingDescriptions: {},
      descriptionCollapseTimers: {},
      descriptionHeights: {},
      overflowingDescriptions: {},
      resizeObserver: null,
      resizeFallbackEnabled: false,
      overflowMeasurementFrame: null
    };
  },

  computed: {
    ...mapState('campaign', ['settings']),
    ...mapState('publicOrgSettings', {
      defaultSettings: (state) => state.settings
    }),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapGetters('publicOrgSettings', ['minimumDonationAmount']),

    settingsComputed() {
      if (this.formCampaignSettings) {
        return this.formCampaignSettings;
      }

      return this.staticForm ? this.defaultSettings : this.settings;
    },

    donationItems() {
      const items = this.settingsComputed?.donationItems?.length ? this.settingsComputed.donationItems : this.defaultSettings?.donationItems || [];

      return items.map((item) => ({
        ...item,
        amount: this.itemAmount(item),
        currency: this.donation.currency
      }));
    },

    selectedDonationItems() {
      return this.staticForm ? this.localDonationItems : this.donation.donationItems;
    },

    selectedItemsTotal() {
      return this.selectedDonationItems.reduce((total, item) => {
        return total + Number(item.amount || 0) * Number(item.quantity || 0);
      }, 0);
    },

    otherAmount: {
      get() {
        return this.staticForm ? this.localDonationItemsOtherAmount : this.donation.donationItemsOtherAmount;
      },
      set(value) {
        const amount = Number(value);
        const normalizedAmount = Number.isFinite(amount) && amount > 0 ? amount : null;

        if (this.staticForm) {
          this.$emit('update:local-donation-items-other-amount', normalizedAmount);
          return;
        }

        this.SET_DONATION_ITEMS_OTHER_AMOUNT(normalizedAmount);
      }
    },

    selectedTotal() {
      return this.selectedItemsTotal + Number(this.otherAmount || 0);
    },

    isOtherAmountSelected() {
      return Number(this.otherAmount || 0) > 0;
    },

    showOtherButton() {
      return !this.settingsComputed?.hideOtherButton || this.isOtherAmountSelected || this.isOtherInputOpen;
    },

    showOtherInput() {
      return this.isOtherInputOpen || this.isOtherAmountSelected;
    },

    effectiveMinimumAmount() {
      const campaignMinimum = this.settingsComputed?.minimumDonationAmount;

      if (campaignMinimum != null) {
        return Number(campaignMinimum);
      }

      return this.minimumDonationAmount;
    },

    amountValidationMessage() {
      if (!this.showError) {
        return '';
      }

      if (!this.selectedTotal) {
        if (this.isOtherInputOpen) {
          return 'Please enter an amount';
        }

        return this.settingsComputed?.hideOtherButton ? 'Please select an item to continue' : 'Please select an item or enter an amount to continue';
      }

      if (this.selectedTotal >= this.effectiveMinimumAmount) {
        return '';
      }

      return `Minimum donation amount is ${getCurrencySymbol(this.donation.currency)}${this.effectiveMinimumAmount}`;
    }
  },

  watch: {
    selectedTotal: {
      immediate: true,
      handler(value) {
        if (this.staticForm) {
          this.$emit('update:local-amount', value > 0 ? value : null);
          return;
        }

        if (value > 0) {
          this.SET_AMOUNT(value);
          this.SET_AMOUNT_DESCRIPTION(null);
          return;
        }

        this.RESET_AMOUNT();
      }
    },
    donationItems: {
      immediate: true,
      handler() {
        this.syncSelectedItemsWithInventory();
        this.$nextTick(this.measureDescriptionOverflow);
      }
    },
    active() {
      this.$nextTick(this.measureDescriptionOverflow);
    }
  },

  mounted() {
    this.measureDescriptionOverflow();

    const section = this.$refs['form-donation-items'];
    if (window.ResizeObserver && section) {
      this.resizeObserver = new window.ResizeObserver(this.scheduleDescriptionOverflowMeasurement);
      this.resizeObserver.observe(section);
      return;
    }

    window.addEventListener('resize', this.scheduleDescriptionOverflowMeasurement);
    this.resizeFallbackEnabled = true;
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.resizeFallbackEnabled) {
      window.removeEventListener('resize', this.scheduleDescriptionOverflowMeasurement);
    }

    if (this.overflowMeasurementFrame) {
      window.cancelAnimationFrame(this.overflowMeasurementFrame);
    }

    Object.values(this.descriptionCollapseTimers).forEach((timer) => window.clearTimeout(timer));
  },

  methods: {
    ...mapMutations('donation', ['SET_AMOUNT', 'RESET_AMOUNT', 'SET_AMOUNT_DESCRIPTION', 'SET_DONATION_ITEM_QUANTITY', 'SET_DONATION_ITEMS_OTHER_AMOUNT']),

    currencySymbol(item = {}) {
      return getCurrencySymbol(item.currency || this.donation.currency);
    },

    itemAmount(item) {
      const amount = Number(item.amount || 0);
      const baseCurrency = item.currency;
      const targetCurrency = this.donation.currency;

      if (!baseCurrency || baseCurrency === targetCurrency) {
        return amount;
      }

      if (!item.autoConvert) {
        const currencyAmount = Number(item.currencyAmounts?.[targetCurrency]);
        return !Number.isNaN(currencyAmount) && currencyAmount >= 0 ? currencyAmount : amount;
      }

      const conversionRates = this.settingsComputed?.conversionRates || this.defaultSettings?.conversionRates || [];
      const exchangeRateObj =
        Array.isArray(conversionRates) && conversionRates.find((rate) => rate.fromCurrency === baseCurrency && rate.toCurrency === targetCurrency);
      const conversionRate = Number(exchangeRateObj?.rate);

      if (!conversionRate) {
        return amount;
      }

      return Math.round(amount * conversionRate);
    },

    hasImage(item) {
      return Boolean(item.imageUrl) && this.failedImageUrls[item.id] !== item.imageUrl;
    },

    handleImageError(item) {
      this.$set(this.failedImageUrls, item.id, item.imageUrl);
    },

    selectedQuantity(item) {
      return this.selectedDonationItems.find((selection) => selection.id === item.id)?.quantity || 0;
    },

    descriptionKey(item) {
      return String(item.id);
    },

    isDescriptionExpanded(item) {
      return Boolean(this.expandedDescriptions[this.descriptionKey(item)]);
    },

    isDescriptionCollapsing(item) {
      return Boolean(this.collapsingDescriptions[this.descriptionKey(item)]);
    },

    hasDescriptionOverflow(item) {
      return Boolean(this.overflowingDescriptions[this.descriptionKey(item)]);
    },

    shouldShowDescriptionToggle(item) {
      return this.hasDescriptionOverflow(item) || this.isDescriptionExpanded(item);
    },

    descriptionStyle(item) {
      const key = this.descriptionKey(item);
      const height = this.descriptionHeights[key];

      if (!height) {
        return null;
      }

      return {
        '--description-expanded-height': `${height.expanded}px`,
        '--description-collapsed-height': `${height.collapsed}px`
      };
    },

    toggleDescription(item) {
      const key = this.descriptionKey(item);
      if (this.isDescriptionExpanded(item)) {
        if (this.descriptionCollapseTimers[key]) {
          window.clearTimeout(this.descriptionCollapseTimers[key]);
        }

        this.$set(this.collapsingDescriptions, key, true);
        this.$delete(this.expandedDescriptions, key);
        const timer = window.setTimeout(() => {
          this.$delete(this.collapsingDescriptions, key);
          this.$delete(this.descriptionCollapseTimers, key);
        }, 220);
        this.$set(this.descriptionCollapseTimers, key, timer);
        return;
      }

      if (this.descriptionCollapseTimers[key]) {
        window.clearTimeout(this.descriptionCollapseTimers[key]);
        this.$delete(this.descriptionCollapseTimers, key);
      }

      this.$delete(this.collapsingDescriptions, key);
      this.$set(this.expandedDescriptions, key, true);
    },

    scheduleDescriptionOverflowMeasurement() {
      if (this.overflowMeasurementFrame) {
        return;
      }

      this.overflowMeasurementFrame = window.requestAnimationFrame(() => {
        this.overflowMeasurementFrame = null;
        this.measureDescriptionOverflow();
      });
    },

    measureDescriptionOverflow() {
      const refs = this.$refs.descriptionTexts || [];
      const descriptionElements = Array.isArray(refs) ? refs : [refs];
      const nextDescriptionHeights = {};
      const nextOverflowingDescriptions = {};

      descriptionElements.forEach((element) => {
        if (!element?.dataset?.itemId) {
          return;
        }

        const styles = window.getComputedStyle(element);
        const fontSize = Number.parseFloat(styles.fontSize) || 0;
        const lineHeight = Number.parseFloat(styles.lineHeight) || fontSize * 1.2;
        const overflowLineCount = Number(element.dataset.overflowLineCount) || 2;
        const collapsedHeight = Math.ceil(lineHeight);
        const expandedHeight = this.measureExpandedDescriptionHeight(element);
        const overflowHeight = Math.ceil(lineHeight * overflowLineCount);
        nextDescriptionHeights[element.dataset.itemId] = {
          collapsed: collapsedHeight,
          expanded: expandedHeight
        };

        if (expandedHeight > overflowHeight + 1) {
          nextOverflowingDescriptions[element.dataset.itemId] = true;
        }
      });

      const currentKeys = Object.keys(this.overflowingDescriptions);
      const nextKeys = Object.keys(nextOverflowingDescriptions);
      const hasChanged =
        currentKeys.length !== nextKeys.length || nextKeys.some((key) => this.overflowingDescriptions[key] !== nextOverflowingDescriptions[key]);
      const currentHeightKeys = Object.keys(this.descriptionHeights);
      const nextHeightKeys = Object.keys(nextDescriptionHeights);
      const heightsChanged =
        currentHeightKeys.length !== nextHeightKeys.length ||
        nextHeightKeys.some((key) => {
          const currentHeight = this.descriptionHeights[key];
          const nextHeight = nextDescriptionHeights[key];
          return currentHeight?.collapsed !== nextHeight.collapsed || currentHeight?.expanded !== nextHeight.expanded;
        });

      if (hasChanged) {
        this.overflowingDescriptions = nextOverflowingDescriptions;
      }

      if (heightsChanged) {
        this.descriptionHeights = nextDescriptionHeights;
      }

      Object.keys(this.expandedDescriptions).forEach((key) => {
        if (!nextOverflowingDescriptions[key]) {
          this.$delete(this.expandedDescriptions, key);
          this.$delete(this.collapsingDescriptions, key);
        }
      });
    },

    measureExpandedDescriptionHeight(element) {
      const width = element.clientWidth || element.offsetWidth;
      const clone = element.cloneNode(true);

      clone.classList.remove('collapsed', 'expanded');
      Object.assign(clone.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        left: '0',
        top: '0',
        width: width ? `${width}px` : 'auto',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        whiteSpace: 'normal',
        textOverflow: 'clip',
        overflowWrap: 'anywhere',
        display: 'block'
      });

      element.parentNode.appendChild(clone);
      const expandedHeight = Math.ceil(clone.scrollHeight);
      clone.remove();

      return expandedHeight;
    },

    quantityLimit(item) {
      const limits = [];
      const maxPerDonation = this.integerLimit(item.maxPerDonation);
      const availableInventory = this.integerLimit(item.availableInventory);

      if (!item.allowQuantity) {
        limits.push(1);
      }

      if (maxPerDonation !== null) {
        limits.push(maxPerDonation);
      }

      if (availableInventory !== null) {
        limits.push(availableInventory);
      }

      return limits.length ? Math.max(Math.min(...limits), 0) : Infinity;
    },

    integerLimit(value) {
      if (value === null || value === undefined) {
        return null;
      }

      const number = Number(value);
      return Number.isInteger(number) ? number : null;
    },

    isSoldOut(item) {
      return this.quantityLimit(item) === 0;
    },

    canIncrement(item) {
      return this.selectedQuantity(item) < this.quantityLimit(item);
    },

    syncSelectedItemsWithInventory() {
      const availableItemsById = new Map(this.donationItems.map((item) => [item.id, item]));

      [...this.selectedDonationItems].forEach((selection) => {
        const item = availableItemsById.get(selection.id);
        if (!item) {
          this.setDonationItemQuantity(selection, 0);
          return;
        }

        const quantityLimit = this.quantityLimit(item);
        if (selection.quantity > quantityLimit) {
          this.setDonationItemQuantity(item, quantityLimit);
          return;
        }

        if (selection.amount !== item.amount || selection.currency !== item.currency) {
          this.setDonationItemQuantity(item, selection.quantity);
        }
      });
    },

    setDonationItemQuantity(item, quantity) {
      if (!this.staticForm) {
        this.SET_DONATION_ITEM_QUANTITY({ item, quantity });
        return;
      }

      const normalizedQuantity = Math.max(Number(quantity) || 0, 0);
      const nextItems = [...this.localDonationItems];
      const existingIndex = nextItems.findIndex((selection) => selection.id === item.id);

      if (!normalizedQuantity) {
        if (existingIndex >= 0) {
          nextItems.splice(existingIndex, 1);
        }
        this.$emit('update:local-donation-items', nextItems);
        return;
      }

      const selection = {
        id: item.id,
        name: item.name,
        imageUrl: item.imageUrl,
        amount: Number(item.amount),
        currency: item.currency,
        quantity: normalizedQuantity
      };

      if (existingIndex >= 0) {
        nextItems.splice(existingIndex, 1, selection);
      } else {
        nextItems.push(selection);
      }

      this.$emit('update:local-donation-items', nextItems);
    },

    selectItem(item) {
      if (this.selectedQuantity(item) > 0) {
        return;
      }

      this.increment(item);
    },

    increment(item) {
      if (!this.canIncrement(item)) {
        return;
      }

      const currentQuantity = this.selectedQuantity(item);
      const nextQuantity = Math.min(currentQuantity + 1, this.quantityLimit(item));
      this.setDonationItemQuantity(item, nextQuantity);
    },

    decrement(item) {
      const nextQuantity = Math.max(this.selectedQuantity(item) - 1, 0);
      this.setDonationItemQuantity(item, nextQuantity);
    },

    async toggleOtherAmountInput() {
      if (this.isOtherInputOpen || this.isOtherAmountSelected) {
        this.otherAmount = null;
        this.isOtherInputOpen = false;
        this.otherAmountValidationMessage = '';
        return;
      }

      this.isOtherInputOpen = true;
      this.otherAmountValidationMessage = '';
      await this.$nextTick();

      const numericInputComponent = this.$refs.otherAmountInput;
      if (numericInputComponent?.inputRef) {
        numericInputComponent.inputRef.focus();
        numericInputComponent.inputRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    handleOtherAmountInputChange() {
      this.otherAmountValidationMessage = '';
    },

    handleOtherAmountValidationError({ message }) {
      this.otherAmountValidationMessage = message;
    },

    selectOtherAmount() {
      if (!this.isOtherAmountSelected) {
        this.isOtherInputOpen = false;
      }
    }
  }
};
</script>

<template>
  <section ref="form-donation-items" :class="{ 'section-has-error': showError && !selectedTotal }" class="form-donation-items mb-8">
    <div v-if="donationItems.length" class="donation-items-list flex flex-col gap-3">
      <button
        v-for="item in donationItems"
        :key="item.id"
        type="button"
        class="donation-item-card flex items-start gap-3 w-full bg-white border border-solid border-gray-disabled rounded-lg cursor-pointer"
        :class="{ disabled: isSoldOut(item), selected: selectedQuantity(item) > 0 }"
        @click="selectItem(item)"
      >
        <span
          class="donation-item-image flex items-center justify-center shrink-0 bg-white border border-solid border-gray-disabled rounded-lg overflow-hidden"
        >
          <img v-if="hasImage(item)" :src="item.imageUrl" :alt="item.name" @error="handleImageError(item)" />
          <img v-else class="donation-item-placeholder" src="https://media.double.giving/images/donation-item-placeholder.svg" alt="" />
        </span>

        <span class="donation-item-content flex items-start justify-between gap-4 flex-1">
          <span class="donation-item-copy flex flex-col items-start flex-1">
            <span class="donation-item-name font-nunito font-bold text-black-00">{{ item.name }}</span>
            <span
              v-if="item.description"
              ref="descriptionTexts"
              class="donation-item-description subtext-small-text-semibold text-black-01"
              :class="{
                collapsed: hasDescriptionOverflow(item) && !isDescriptionExpanded(item),
                expanded: isDescriptionExpanded(item),
                collapsing: isDescriptionCollapsing(item)
              }"
              :data-item-id="descriptionKey(item)"
              data-overflow-line-count="2"
              :style="descriptionStyle(item)"
            >
              {{ item.description }}
            </span>
            <span
              v-if="item.description && shouldShowDescriptionToggle(item)"
              class="donation-item-description-toggle subtext-small-text-semibold"
              role="button"
              tabindex="0"
              @click.stop="toggleDescription(item)"
              @keydown.enter.stop.prevent="toggleDescription(item)"
              @keydown.space.stop.prevent="toggleDescription(item)"
            >
              {{ isDescriptionExpanded(item) ? 'Show less' : 'Show more' }}
            </span>
          </span>

          <span class="donation-item-price-quantity flex flex-col items-end justify-between shrink-0">
            <span class="donation-item-price font-nunito font-bold text-black">
              <span>{{ currencySymbol(item) }}</span>
              <span>{{ item.amount | number }}</span>
            </span>

            <span class="donation-item-quantity flex items-center gap-1">
              <button
                type="button"
                class="quantity-button minus flex items-center justify-center rounded"
                :disabled="!selectedQuantity(item)"
                @click.stop="decrement(item)"
              >
                <span aria-hidden="true"></span>
              </button>
              <span class="quantity-value font-nunito font-bold text-center">{{ selectedQuantity(item) }}</span>
              <button
                type="button"
                class="quantity-button plus flex items-center justify-center rounded"
                :disabled="!canIncrement(item)"
                @click.stop="increment(item)"
              >
                <span aria-hidden="true"></span>
              </button>
            </span>
          </span>
        </span>
      </button>
    </div>

    <div v-if="showOtherButton" class="donation-items-other">
      <button
        type="button"
        class="donation-items-other-button flex flex-col items-center justify-center w-full border border-solid rounded-lg cursor-pointer"
        :class="{ selected: isOtherAmountSelected || isOtherInputOpen }"
        @click="toggleOtherAmountInput"
      >
        <span class="font-nunito font-bold text-black-00">Other amount</span>
      </button>

      <transition name="expandx3">
        <div v-if="showOtherInput" class="donation-items-other-input">
          <numeric-input
            v-model="otherAmount"
            :max="MAX_AMOUNT"
            :min="0.01"
            :currency-symbol="currencySymbol()"
            :show-error-message="false"
            ref="otherAmountInput"
            @input-change="handleOtherAmountInputChange"
            @validation-error="handleOtherAmountValidationError"
            @focusout="selectOtherAmount"
            @keyup.enter="selectOtherAmount"
          />
        </div>
      </transition>
    </div>

    <div class="mt-1">
      <span v-if="otherAmountValidationMessage" class="custom-error-message">{{ otherAmountValidationMessage }}</span>
      <span v-else-if="amountValidationMessage" class="custom-error-message">{{ amountValidationMessage }}</span>
    </div>
  </section>
</template>
